import type { BehaviorConfig, CoordinateSystemConfig, WidgetConfig } from '@examdojo/core/widget';
import type { FeatureStyleNode } from './elements/features';

// =============================================================================
// ELEMENT TYPES
// =============================================================================

/** Function element types supported by Graphpad */
export type FunctionType =
  | 'linear'
  | 'quadratic'
  | 'sine'
  | 'cosine'
  | 'exponential'
  | 'logarithmic'
  | 'absolute'
  | 'cubic'
  | 'sqrt'
  | 'tangent-fn';

/** Feature types - computed from one or more elements */
export type FeatureType =
  | 'roots'
  | 'extrema'
  | 'tangent'
  | 'secant'
  | 'derivative'
  | 'intersection'
  | 'handle'
  | 'yIntercept'
  | 'asymptotes'
  | 'inflection'
  | 'axisOfSymmetry'
  | 'trajectory'
  | 'monotonicity'
  | 'functionArea'
  | 'intersectionArea'
  | 'amplitude'
  | 'wavelength'
  | 'period'
  | 'phaseShift';

/** All possible element types in the system */
export type ElementType = FunctionType | FeatureType;

// =============================================================================
// HANDLE BEHAVIORS
// =============================================================================

/** Handle drag behaviors */
/* In the future we could add more handle behaviors, like rotate, scale, etc. */
export type HandleBehavior =
  | 'translate' // Move freely in x and y
  | 'translate-x' // Move only horizontally
  | 'translate-y'; // Move only vertically

// =============================================================================
// ELEMENT DEFINITIONS (PARAMS)
// Pure data definitions - no display information
// =============================================================================

/** Base element definition */
export interface BaseElementDef {
  type: ElementType;
}

/** Function element definition */
export interface FunctionElementDef<T extends FunctionType = FunctionType> extends BaseElementDef {
  type: T;
  params?: Record<string, number>;
}

/** Feature element definition - references one or more elements */
export interface FeatureElementDef<T extends FeatureType = FeatureType> extends BaseElementDef {
  type: T;
  /** Feature-specific parameters (including `of` dependency reference) */
  params?: Record<string, unknown>;
}

/**
 * Handle element definition.
 * @deprecated Handle is now a feature type. Use FeatureElementDef<'handle'> instead.
 */
export type HandleElementDef = FeatureElementDef<'handle'>;

export type ElementDef = FunctionElementDef | FeatureElementDef;

// =============================================================================
// PARAMS - The Model
// Pure mathematical/logical definitions without any visual configuration
// =============================================================================

/** Element definitions keyed by ID */
export type GraphpadParams = Record<string, ElementDef>;

// =============================================================================
// DISPLAY - The View
// All visual configuration: board appearance + element styling
// =============================================================================

/**
 * Display configuration for an individual element.
 * Uses the FeatureStyleNode pattern for recursive, nested styling.
 *
 * ElementDisplayConfig IS a FeatureStyleNode, enabling:
 * - Element-level styles that cascade to all parts
 * - Subpart-specific overrides
 * - Arbitrarily deep nesting
 *
 * @example
 * ```typescript
 * // Simple element styling
 * const pointConfig: ElementDisplayConfig = {
 *   point: { color: 'sky', size: 8 },
 * };
 *
 * // Feature with subparts
 * const extremaConfig: ElementDisplayConfig = {
 *   point: { size: 6 },
 *   subparts: {
 *     maxima: { point: { color: 'green' } },
 *     minima: { point: { color: 'red' } },
 *   },
 * };
 * ```
 */
export type ElementDisplayConfig = FeatureStyleNode;

/** Complete display configuration for Graphpad */
export interface GraphpadDisplayConfig {
  /** Coordinate system configuration (axes, grid, bounding box) */
  coordinateSystem?: CoordinateSystemConfig;
  /** Display configuration for individual elements, keyed by element ID */
  elements?: Record<string, ElementDisplayConfig>;
}

// =============================================================================
// WIDGET CONFIG
// =============================================================================

/** Complete Graphpad widget configuration */
export type GraphpadWidgetConfig = WidgetConfig<'graphpad', GraphpadParams, GraphpadDisplayConfig, BehaviorConfig>;

// =============================================================================
// TYPE GUARDS
// =============================================================================

const FUNCTION_TYPES: FunctionType[] = [
  'linear',
  'quadratic',
  'sine',
  'cosine',
  'exponential',
  'logarithmic',
  'absolute',
  'cubic',
  'sqrt',
  'tangent-fn',
];
const FEATURE_TYPES: FeatureType[] = [
  'roots',
  'extrema',
  'tangent',
  'secant',
  'derivative',
  'intersection',
  'handle',
  'yIntercept',
  'asymptotes',
  'inflection',
  'axisOfSymmetry',
  'trajectory',
  'monotonicity',
  'functionArea',
  'intersectionArea',
  'amplitude',
  'wavelength',
  'period',
  'phaseShift',
];

export function isFunctionElementDef(def: ElementDef): def is FunctionElementDef {
  return FUNCTION_TYPES.includes(def.type as FunctionType);
}

export function isFeatureElementDef(def: ElementDef): def is FeatureElementDef {
  return FEATURE_TYPES.includes(def.type as FeatureType);
}

export function isHandleElementDef(def: ElementDef): def is HandleElementDef {
  return def.type === 'handle';
}
